"use client";
import { useSearchParams, type ReadonlyURLSearchParams } from "next/navigation";
import getPublicClient from "@/lib/algolia/getPublicClient";
import withAvailabilityFacetCounts from "@/components/Search/utils/withAvailabilityFacetCounts";
import { InstantSearch } from "react-instantsearch";
import { SearchProps } from "@/components/Search/types";
import { PRODUCT_SUMMARY_INDEX_EN } from "@/constants/algolia";
import useSearchInitialState from "./hooks/useSearchInitialState";
import { SearchContextProvider } from "./components/SearchContextProvider";
import SearchContent from "./components/SearchContent";
import DynamicSearchContent from "./components/SearchContent/DynamicSearchContent";
import DynamicExperienceWrapper from "@/components/DynamicExperienceWrapper";
import {
  buildInitialFormState,
  buildObservedFormStateKeys,
} from "@/components/DynamicExperienceWrapper/dcs";
import { resolveDynamicExperienceEnvironment } from "@/components/DynamicExperienceWrapper/environment";
import { getEnv } from "@/env";
import useDynamicExperienceSearch from "@/monarch/rules/dynamicExperienceSearch/useDynamicExperienceSearch";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import { Skeleton } from "../ui/skeleton";

const searchClient = withAvailabilityFacetCounts(getPublicClient());

/**
 * A query the visitor navigated to wins; the CMS-authored `queryString` is the
 * fallback so shortcode pages still search for what they authored.
 * Repeated facet keys are preserved (do not collapse via Object.fromEntries).
 */
export const buildSearchFormState = (
  liveParams: ReadonlyURLSearchParams,
  queryString = "",
): Readonly<Record<string, string>> => {
  const merged = new URLSearchParams(queryString);
  const liveKeys = new Set<string>();

  liveParams.forEach((value, key) => {
    if (value !== "") liveKeys.add(key);
  });

  liveKeys.forEach((key) => {
    merged.delete(key);
    liveParams.getAll(key).forEach((value) => {
      if (value !== "") merged.append(key, value);
    });
  });

  return buildInitialFormState(merged);
};

function Search(props: SearchProps) {
  const nextSearchParams = useSearchParams();
  const initialSearchState = useSearchInitialState(
    PRODUCT_SUMMARY_INDEX_EN,
    props.queryString,
  );

  const { flagValue, isLoading: isFlagLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_SEARCH_DYNAMIC_EXPERIENCE_TEST",
    "legacy",
  );

  const isTestFlag = flagValue === "test";

  const {
    isLoading: isMonarchLoading,
    variant: monarchVariant,
    data: monarchData,
  } = useDynamicExperienceSearch({ enabled: !isFlagLoading && isTestFlag });

  if (isFlagLoading || isMonarchLoading) {
    return <Skeleton variant="fullPage" />;
  }

  if (
    isTestFlag &&
    monarchVariant === "test" &&
    monarchData?.type === "HEN-UI"
  ) {
    return (
      <DynamicExperienceWrapper
        forcedFlowId={monarchData.forcedFlowId}
        environment={resolveDynamicExperienceEnvironment()}
        tagularSourceKey={getEnv("NEXT_PUBLIC_AUTHX_TENANT_ID") || ""}
        initialFormState={buildSearchFormState(
          nextSearchParams,
          props.queryString,
        )}
        urlFormState={buildInitialFormState(nextSearchParams)}
        observedFormStateKeys={buildObservedFormStateKeys()}
      />
    );
  }

  const renderContent = () => {
    if (!isTestFlag || monarchVariant !== "test") {
      return <SearchContent {...props} />;
    }

    return (
      <DynamicSearchContent
        forcedFlowId={monarchData?.forcedFlowId}
        {...props}
      />
    );
  };

  return (
    <SearchContextProvider searchIndex={PRODUCT_SUMMARY_INDEX_EN}>
      <InstantSearch
        searchClient={searchClient}
        indexName={PRODUCT_SUMMARY_INDEX_EN}
        initialUiState={initialSearchState}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        {renderContent()}
      </InstantSearch>
    </SearchContextProvider>
  );
}

export default Search;
